import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'
import { t } from 'ttag'

import { List } from 'components/shared/List/List'
import { Button } from 'components/shared/Button/Button'

import styles from './CollapsableList.module.scss'

type CollapsableListProps = {
  buttonClassName?: string
  buttonLabels?: [React.ReactNode, React.ReactNode]
  children?: React.ReactNode
  className?: string
  limit?: number
  tagName?: React.ComponentProps<typeof List>['tagName']
  isInitiallyCollapsed?: boolean
}

export const CollapsableList = ({
  buttonClassName,
  buttonLabels = [t`Show more...`, t`Show less...`],
  children,
  className,
  limit = 5,
  tagName,
  isInitiallyCollapsed = true,
}: CollapsableListProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(isInitiallyCollapsed)

  const [showMoreLabel, showLessLabel] = buttonLabels

  const isLimitExceeded = R.gt(React.Children.count(children), limit)

  const handleAction = React.useCallback(() => {
    setIsCollapsed(R.not)
  }, [setIsCollapsed])

  return (
    <React.Fragment>
      <List
        className={cx(styles.list, className)}
        direction="column"
        {...{ tagName }}
      >
        {isLimitExceeded && isCollapsed
          ? R.addIndex(R.filter)(
              (value, index) => (R.lte(index + 1, limit) ? value : null),
              React.Children.toArray(children),
            )
          : children}
      </List>
      {isLimitExceeded ? (
        <Button
          className={cx(styles.button, buttonClassName)}
          label={isCollapsed ? showMoreLabel : showLessLabel}
          size="xsmall"
          variant="text"
          onClick={handleAction}
        />
      ) : null}
    </React.Fragment>
  )
}

CollapsableList.Item = List.Item
